import type { DOMAttributes } from 'react';
import React from 'react';

import type { Link, Others, Texts } from '@rescui/typography';
import type {
  Digits,
  LinkParams,
  Subtitles,
  TextsParams,
} from '@rescui/typography/lib/create-text-cn';
import type { Theme } from '@rescui/ui-contexts/lib/parts/theme-context';

type BaseProps = {
  component?:
    | 'a'
    | 'span'
    | 'p'
    | 'div'
    | 'label'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6';
  theme?: Theme;
  children?: React.ReactNode;
  className?: string;
};

type TextProps = {
  variant?: Texts | Subtitles | Digits;
  params?: TextsParams;
};

type LinkProps = {
  variant?: Link;
  params?: LinkParams;
};

type OtherProps = {
  variant?: Others;
  params?: undefined;
};

type Props = BaseProps &
  (TextProps | LinkProps | OtherProps) &
  DOMAttributes<any>;

const DEFAULT_COMPONENTS = {
  'rs-h1': 'h1',
  'rs-h2': 'h2',
  'rs-h3': 'h3',
  'rs-h4': 'h4',
  'rs-h5': 'h5',
  'rs-h6': 'h6',
};

/**
 * Simplified implementation of RescUI typography without context/theme support, uses only css classes, but can
 * be used in React Server Components.
 */
const IaTypography: React.FC<Props> = ({
  component = 'span',
  variant = 'rs-text-2',
  theme = 'light',
  children,
  params,
  className,
  ...otherProps
}) => {
  const classes: string[] = [variant];
  classes.push(`${variant}_theme_${theme}`);

  if (params) {
    const convert = (key: string, value: any) => {
      const dashed = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      const isBoolean = typeof value === 'boolean';
      const suffix = isBoolean ? '' : `_${value}`;
      return `${variant}_${dashed}${suffix}`;
    };

    classes.push(
      Object.entries(params)
        .map(([k, v]) => convert(k, v))
        .join(' '),
    );
  }

  if (className) {
    classes.push(className);
  }

  return React.createElement(
    DEFAULT_COMPONENTS?.[variant] || component,
    { className: classes.join(' '), ...otherProps },
    children,
  );
};

export default IaTypography;
